/**
 * Amplify configuration for the Subscriber portal.
 *
 * Per ADR-016:
 *   - User pool: nexa-subscriber-pool (separate from staff pool)
 *   - App client: PKCE flow, no client secret, no SSO
 *   - Optional MFA (TOTP or passkey) — not enforced
 *   - 8-char minimum password
 *
 * Token storage — ADR-092 D4 (supersedes ADR-016's sessionStorage): Amplify's
 * Cognito tokens are held in a HOST-SCOPED COOKIE on app.nexanomics.com, not in
 * sessionStorage. This is what fixes the embed sign-in handoff:
 *
 *   The /embed/sign-in journey runs on the app origin inside a cross-origin
 *   <iframe> framed by www.nexanomics.com. app.* and www.* share the registrable
 *   domain nexanomics.com, so the iframe is SAME-SITE (cross-origin, same eTLD+1)
 *   — its cookies are NOT third-party-partitioned. A token cookie written while
 *   signing in inside the iframe therefore lands in the real app.nexanomics.com
 *   cookie jar, and is present the moment the shell navigates the TOP window to
 *   app.nexanomics.com/ — the portal loads authenticated. sessionStorage was
 *   per-window and never crossed that boundary (the bug).
 *
 * Cookie attributes (D4):
 *   - HOST-ONLY (no Domain attribute) → scoped to exactly app.nexanomics.com and
 *     NOT the sibling ops.nexanomics.com (never leak the subscriber session to the
 *     staff origin). Do NOT set domain: '.nexanomics.com'.
 *   - Secure — only over https. In local dev (http://localhost) Secure cookies are
 *     never stored, so secure is conditioned on the page protocol.
 *   - SameSite=Lax — the cookie is read by same-origin JS (Amplify), never sent on
 *     a cross-site request (portal-api uses a bearer Authorization header), so Lax
 *     is both sufficient and the safer choice over None.
 *   - expires = 30 days, aligned to the Cognito refresh-token validity
 *     (SubscriberAppClient refreshTokenValidity, cdk/lib/nexa-platform-stack.ts).
 *
 * Sign-out clears these cookies: Amplify's signOut() calls CookieStorage.clear(),
 * which removes the token cookies (verified by the auth-handoff proof).
 */
import { Amplify, type ResourcesConfig } from 'aws-amplify';
import { cognitoUserPoolsTokenProvider } from 'aws-amplify/auth/cognito';
import { CookieStorage } from 'aws-amplify/utils';
// ⚠ TYPE-ONLY, DELIBERATELY. `zone-session` imports `ENV` from THIS module, so a runtime
// import back the other way would be a genuine cycle — and `ENV` is a module-level const,
// so whichever module evaluated second would see it `undefined`. A type import is erased at
// compile time and cannot cycle. The caller passes the zone in; this module never reaches
// out for it.
import type { ZoneConfig } from './lib/zone-session';

const env = import.meta.env;

/** ADR-092 D4 — the host-scoped token cookie store. Host-only (no domain),
 *  Secure over https, SameSite=Lax, 30-day expiry = refresh-token lifetime. */
function subscriberCookieStorage(): CookieStorage {
  const isHttps =
    typeof window !== 'undefined' && window.location?.protocol === 'https:';
  return new CookieStorage({
    // domain intentionally omitted → host-only cookie on the current host
    // (app.nexanomics.com in prod), never shared with ops.nexanomics.com.
    path: '/',
    expires: 30,
    secure: isHttps,
    sameSite: 'lax',
  });
}

/**
 * ADR-120 Amendment 4 — Amplify's pool is now RESOLVED, not fixed at build time.
 *
 * ⭐ WHY THIS FUNCTION BECAME RE-ENTRANT. The platform serves every zone from one domain
 * (`app.nexanomics.com`), but a customer's Cognito pool is per-zone. So the pool this portal
 * authenticates against is a property of the PERSON, discovered from their email at sign-in,
 * not a property of the build. `configureAmplify()` is therefore called twice on a
 * foreign-zone sign-in: once at boot with whatever zone the session already knows, and again
 * the moment the visitor's real zone is resolved, before `signIn()` runs.
 *
 * ⚠ `Amplify.configure()` REPLACES the config wholesale, so the cookie token provider has to
 * be re-attached on every call — it is set below, after configure, for exactly that reason.
 * Dropping it on the second call would silently move the session's tokens back to Amplify's
 * default storage and break the embed sign-in handoff ADR-092 D4 exists to fix.
 *
 * Passing no argument uses the session's active zone, which at boot is the persisted zone if
 * there is one and the build zone otherwise.
 */
export function configureAmplify(zone?: ZoneConfig): void {
  const userPoolId = zone?.userPoolId ?? env.VITE_SUBSCRIBER_POOL_ID;
  const userPoolClientId = zone?.userPoolClientId ?? env.VITE_SUBSCRIBER_POOL_CLIENT_ID;

  if (!userPoolId || !userPoolClientId) {
    console.error(
      'portal-subscriber: missing VITE_SUBSCRIBER_POOL_ID or VITE_SUBSCRIBER_POOL_CLIENT_ID — copy .env.example to .env.local and populate from CDK outputs',
    );
  }

  const config: ResourcesConfig = {
    Auth: {
      Cognito: {
        userPoolId: userPoolId ?? 'unconfigured',
        userPoolClientId: userPoolClientId ?? 'unconfigured',
        loginWith: {
          email: true,
        },
        // Optional MFA — TOTP enabled, SMS disabled. Subscribers can enrol via
        // account settings; the platform does not force it.
        mfa: {
          status: 'optional',
          totpEnabled: true,
          smsEnabled: false,
        },
        userAttributes: { email: { required: true } },
        passwordFormat: {
          minLength: 8,
          requireLowercase: true,
          requireUppercase: true,
          requireNumbers: true,
          requireSpecialCharacters: true,
        },
      },
    },
  };
  Amplify.configure(config);
  /**
   * ⛔⛔ THE TOKEN STORE KEEPS ITS OWN COPY OF THE POOL CONFIG, AND `Amplify.configure()` ONLY
   * SETS IT THE FIRST TIME. From the second call on, Amplify takes its "preserve the configured
   * Auth library options" branch (aws-amplify `initSingleton.configure`) and never re-points
   * `cognitoUserPoolsTokenProvider` — whose `authConfig` names the cookie KEY
   * (`CognitoIdentityServiceProvider.<clientId>.…`) and the client token refresh uses.
   *
   * So a US sign-in authenticated against the US pool and then WROTE ITS TOKENS UNDER THE AU
   * CLIENT'S KEY. The next page load booted into the persisted US zone, looked under the US key,
   * found nothing, and signed the customer out — every refresh, every link, every new tab.
   * Observed live 23 September 2026 (`e2e/us-subscriber-sign-in.mjs` A3.2; cookies keyed
   * `1i1jc7…`, the AU subscriber client, holding a us-east-1 identity's tokens). Invisible for
   * AU customers, because re-pointing at the same client changes nothing.
   */
  cognitoUserPoolsTokenProvider.setAuthConfig(config.Auth!);
  cognitoUserPoolsTokenProvider.setKeyValueStorage(subscriberCookieStorage());
}

export const ENV = {
  PORTAL_API_BASE_URL: env.VITE_PORTAL_API_BASE_URL,
  POW_API_BASE_URL: env.VITE_POW_API_BASE_URL,
  // ⛔ The `nexa-signup` widget — public sign-up AND customer sign-in (2 October 2026), verified
  // with `nexa/turnstile-secret-portal` by `signup-api` and `nexa-subscriber-pre-auth`. Customer
  // sign-in is REFUSED without a verified token, so an empty key here is a lockout: deploy.yml
  // fails the build first. Cloudflare's always-pass TEST key is a DEV-SERVER fallback only.
  TURNSTILE_SITE_KEY_PORTAL:
    (env.VITE_TURNSTILE_SITE_KEY_PORTAL as string | undefined) || (env.DEV ? '1x00000000000000000000AA' : ''),
  // ADR-080 Amendment 1 — the SOW availability gate's public state endpoint
  // (the sow-gate Worker's GET /gate/state). Read by SowEmbedPage to decide whether
  // to show the sign-up journey or the beta-interest gate modal (UX only —
  // enforcement is server-side). Defaults to the deployed Worker.
  SOW_GATE_STATE_URL:
    (env.VITE_SOW_GATE_STATE_URL as string | undefined) ||
    'https://sow-gate.jeff-herbert.workers.dev/gate/state',
  // Punchlist #9 A — the beta-invites gate's public state endpoint (the same
  // Worker, a different KV key). Read by the Beta Programme portal to decide
  // whether to offer Start BOW (UX only — POST /portal/bow/launch enforces it).
  BETA_GATE_STATE_URL:
    (env.VITE_BETA_GATE_STATE_URL as string | undefined) ||
    'https://sow-gate.jeff-herbert.workers.dev/gate/beta/state',
  // ADR-104 Layer 1 — the sow-gate Worker's POST /embed/health failure beacon.
  // Written by the framed embed pages when the form renders but then fails
  // (submit-error / turnstile-error / gate-unreadable) — the three faults no
  // server log and no synthetic probe can see. Same Worker as the two gates
  // above; derived from the gate URL so a Worker move cannot leave this pointing
  // at the old host (the one URL that must never drift independently of them).
  EMBED_HEALTH_URL:
    (env.VITE_EMBED_HEALTH_URL as string | undefined) ||
    ((env.VITE_SOW_GATE_STATE_URL as string | undefined) || 'https://sow-gate.jeff-herbert.workers.dev/gate/state')
      .replace(/\/gate\/state$/, '/embed/health'),
};
